<template>
    <form
        ref="form"
        @submit.prevent="submit"
        :action="formRoute"
        method="POST"
        class="relative h-full"
    >
        <page-header
            :submit-button-text="data ? 'Update' : 'Create'"
            :show-submit-button="true"
            :submit-button-disabled="submitButtonDisabled || !editable"
            submit-button-type="submit"

            :has-lock="hasLock"
            :document-lock="documentLock"

            :can-delete="canDelete"
            :delete-route="deleteRoute"
            :delete-value="deleteValue"

            :can-create="canCreate"
            :create-route="createRoute"
            :create-value="createValue"

            :index-route="indexRoute"
            :index-value="indexValue"
            :title="title"
            :subtitle="subtitle"

            :show-preview-button="shouldShowPreviewButton()"
            @click-preview="submitPreviewSite(form.language.iso, form.uri)"

            :lockable="lockable"
            @force-takeover="attemptTakeover"
            @takeover="takeOver"

            :errors="errors"
            :is-slide-over="isSlideOver"

            :seo-data="data ? data.latest_seo : null"

            model="App\Models\CasinoReview"

            :version-id="versionId"
            :init-version-id="initVersionId"
            :last-updated-by="lastUpdatedBy"
        />

        <input-rich-text
            name="editor_note"
            label="Editor's note"
            v-model="form.editor_note"
            :language="form.language"
            :error="errors.editor_note ? errors.editor_note[0] : null"
            :maxlength="60000"
        />
        <p class="my-1 text-xs text-gray-600 dark:text-gray-300 mb-3">The note is for internal use only and won’t be displayed on the site</p>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">* Casino data taken from:</p>
            <reference
                create-text="Create new casino and link casino to this page"
                link-text="Link an existing casino"
                empty-message="All Available Casinos are Already Allocated to a Review"
                :two-step-create="true"
                :creatable="false"
                :language="form.language"
                :should-open-new-window="true"
                v-model="form.casino"
                model-data="Casino"
                :error="errors.casino_id ? errors.casino_id[0] : null"
                :with-docs="[
                    'restrictedCountries',
                    'bonuses.affiliateLink',
                    'bonuses.types',
                    'bonuses.event',
                    'bonuses.countries'
                    ]"
                :query="{
                    no_casino_review: true,
                    variant_of: baseDocumentCasinoId
                }"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

       <input-text
            :disabled="casinoNotSelected"
            name="review_score"
            label="Review Score"
            v-model="form.review_score"
            class="mt-2"
            :error="errors.review_score ? errors.review_score[0] : null"
        />
        <base-page-fields
            :disabled="casinoNotSelected"
            class="mt-3"
            :form="form"
            :errors="errors"
            :editable-timestamps="true"
            :publishable="publishable"
        ></base-page-fields>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Main Bonuses</p>
            <multi-reference
                create-text="Create new bonus and link bonus to this page"
                link-text="Link an existing bonus"
                :disabled="casinoNotSelected"
                :creatable="false"
                v-model="form.bonuses"
                :should-open-new-window="true"
                model-data="Bonus"
                :language="form.language"
                :query="{
                    casino_id: form.casino_id
                }"
                :pre-filled="{
                    casino: form.casino || null,
                }"
                :order="{
                    'name': 'asc'
                }"
                :error="errors.bonuses ? errors.bonuses[0] : null"
            />
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Author</p>
            <reference
                create-text="Create new author and link author to this page"
                link-text="Link an existing author profile"
                :disabled="casinoNotSelected"
                two-step-create
                :creatable="false"
                :language="form.language"
                :should-open-new-window="true"
                v-model="form.author"
                model-data="Author"
                :query="{
                    status_id: 1
                }"
                :order="{
                    'authors.name': 'asc'
                }"
                :excluded-ids="excludedAuthors" />
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Fact checked by</p>
            <reference
                create-text="Create new author and link author to this page"
                link-text="Link an existing author profile"
                :disabled="casinoNotSelected"
                two-step-create
                :creatable="false"
                :should-open-new-window="true"
                v-model="form.factChecker"
                model-data="Author"
                :query="{
                    status_id: 1
                }"
                :order="{
                    'authors.name': 'asc'
                }"
                :language="form.language"
                :excluded-ids="excludedFactCheckers" />
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Published For</p>
            <reference
                create-text="Create new country and link country to this page"
                link-text="Link an existing country"
                :disabled="casinoNotSelected"
                two-step-create
                :creatable="false"
                :should-open-new-window="true"
                :language="form.language"
                v-model="form.publishedFor"
                model-data="Country"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full px-4 pb-3 shadow-sm max-w-4xl mt-3"
        >

            <input-rich-text
                :disabled="casinoNotSelected"
                name="overview"
                label="Intro Text (below H1)"
                v-model="form.overview"
                :language="form.language"
                :error="errors.overview ? errors.overview[0] : null"
                :maxlength="5000"
            />
            <p class="my-1 text-xs text-gray-600 dark:text-gray-200">You can use these available shortcodes in Intro Text (below H1) field: [current_month], [month], [current_year], [year] and [current_abrev_month]</p>

            <Select
                :disabled="casinoNotSelected"
                label="Add Trust Badge"
                name="overview_trust_badge"
                class="mt-3"
                v-model="form.overview_trust_badge"
                :options="[
                        { value: '', name: 'No Badge'},
                        { value: 'verified-experts', name: 'Verified experts for'},
                        { value: 'safety-checked', name: 'Safety checked by'},
                        { value: 'approved', name: 'Approved by'},
                        { value: 'tested', name: 'Tested by'},
                    ]"
                :error="errors.overview_trust_badge ? errors.overview_trust_badge[0] : null"
           />
        </div>

        <div class="mt-3">
            <rocket-table
                :language="form.language"
                v-model="form.table"
                :errors="errors.table ? errors.table[0] : null"
            />
        </div>

        <div v-if="form.casino" class="mt-3">
            <table-base
                v-model="form.two_col_four_table"
                :casino="form.casino"
                :language="form.language"
                :error="errors.two_col_four_table ? errors.two_col_four_table[0] : null"
            ></table-base>
        </div>

         <input-rich-text
         :disabled="casinoNotSelected"

            label="Author Recommendation"
            name="author_recommendation"
            v-model="form.author_recommendation"
            class="mt-2"
            :language="form.language"
            :error="errors.author_recommendation ? errors.author_recommendation[0] : null"
            :maxlength="5000"
        />

         <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Pros and Cons</p>
            <reference
                create-text="Create new pros and cons and link pros and cons to this page"
                link-text="Link an existing pros and cons"
                :disabled="casinoNotSelected"
                :linkable="false"
                :should-open-new-window="true"
                :editable="true"
                :language="form.language"
                v-model="form.prosAndCons"
                model-data="Pros and Cons"
            />
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full px-4 pb-3 shadow-sm max-w-4xl mt-3"
        >

            <media-reference
                :disabled="casinoNotSelected"
                v-model="form.image"
                :language="form.language"
                class="mt-3"
                label="Main Screenshot"
                :media-types="['Image', 'Screenshot']"
            />

            <Select
                :disabled="casinoNotSelected"
                label="Add Trust Badge"
                name="overview_trust_badge"
                class="mt-3"
                v-model="form.image_trust_badge"
                :options="[
                        { value: '', name: 'No Badge'},
                        { value: 'verified-experts', name: 'Verified experts for'},
                        { value: 'safety-checked', name: 'Safety checked by'},
                        { value: 'approved', name: 'Approved by'},
                        { value: 'tested', name: 'Tested by'},
                    ]"
                :error="errors.image_trust_badge ? errors.image_trust_badge[0] : null"
            />

            <div
                class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
            >
                <p class="text-gray-700 dark:text-gray-200 mb-4">Affiliate link for screenshot</p>

                <rocket-link
                    :language="form.language"
                    v-model="form.image_link"
                    class="mt-5"
                    label=""
                    :link-types="[4]"
                    :disabled="casinoNotSelected"
                    :hide-link-text="true"
                    :resettable="true"
                    :error="errors.image_link ? errors.image_link[0] : null"
                />
            </div>
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Slot Reviews</p>
            <multi-reference
                create-text="Create new slot review and link slot review to this page"
                link-text="Link existing slot reviews"
                :disabled="casinoNotSelected"
                :language="form.language"
                :should-open-new-window="true"
                :creatable="false"
                :max-items="6"
                v-model="form.slot_reviews"
                model-data="Slot Review"
                :query="{
                    'status_id': 1
                }"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Game Reviews</p>
            <multi-reference
                create-text="Create new game review and link game review to this page"
                link-text="Link existing game reviews"
                :disabled="casinoNotSelected"
                :language="form.language"
                :should-open-new-window="true"
                :creatable="false"
                :max-items="6"
                v-model="form.game_reviews"
                model-data="Game Review"
                :query="{
                    'status_id': 1
                }"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Similar Casinos</p>
            <multi-reference
                create-text="Create new similar casino and link similar casino to this page"
                link-text="Link existing similar casinos"
                :disabled="casinoNotSelected"
                :max-items="4"
                :creatable="false"
                :two-step-create="true"
                :language="form.language"
                :should-open-new-window="true"
                v-model="form.similarCasinos"
                field-name="similarCasinos"
                model-data="Casino"
                :query="{
                    'casinoReview.status_id': 1
                }"
                :order="{
                    'name': 'asc'
                }"
                :excluded-ids="excludedSimilarCasinos"
            />
        </div>

        <div class="mt-2">
            <span class="text-gray-700 dark:text-gray-200">Content Blocks</span>
            <body-content
                :disabled="casinoNotSelected"
                :gallery="form.gallery"
                :language="form.language"
                v-model="form.contentBlocks"
                :should-show-market-data="true"
                :document-class="className"
                :document-id="data ? data.id : null"
                :errors="errors"
                :content-section-with-how-to="contentSectionWithHowTo"
                :content-section-with-bonus-list-top="contentSectionWithBonusListTop"
                :content-section-with-bonus-list-bottom="contentSectionWithBonusListBottom"
                :content-sections-with-ranking="contentSectionsWithRanking"
                @add-content-block="addContentBlock"
                @add-content-section="addContentSection"
                @delete-content-block="deleteContentBlock"
                @delete-content-section="deleteContentSection"
                @load-content-blocks="loadContentBlocks"
                @add-how-to="howToAdded"
                @delete-how-to="howToDeleted"
                @add-bonus-list-top="bonusListTopAdded"
                @delete-bonus-list-top="bonusListTopDeleted"
                @add-bonus-list-bottom="bonusListBottomAdded"
                @delete-bonus-list-bottom="bonusListBottomDeleted"
                @add-ranking="rankingAdded"
                @delete-ranking="rankingDeleted"
            ></body-content>
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Gallery</p>
            <multi-reference
                create-text="Create new gallery and link gallery to this page"
                link-text="Link an existing gallery"
                :disabled="casinoNotSelected"
                :language="form.language"
                :creatable="false"
                :linkable="false"
                :deletable="true"
                v-model="form.gallery"
                model-data="Media"
            />
        </div>

        <input type="hidden" name="_token" :value="csrf" />
        <input v-if="data" type="hidden" name="_method" value="patch" />
    </form>
</template>

<script>
import crudForm from "../mixins/crudForm";
import pageFormData from "../mixins/pageFormData";
import contentBlocksForm from "../mixins/contentBlocksForm";

export default {
    mixins: [crudForm, contentBlocksForm, pageFormData],
    name: "CasinoReviewForm",
    data() {
        return {
            form: {
                editor_note: this.getFormData("editor_note"),
                h1_anchor: this.getFormData("h1_anchor"),
                review_score: this.getFormData("review_score"),
                overview: this.getFormData("overview"),
                overview_trust_badge: this.getFormData("overview_trust_badge"),
                author_recommendation: this.getFormData("author_recommendation"),
                author_id: this.getFormData("author_id"),
                author: this.getFormData("author"),
                fact_checker_id: this.getFormData("fact_checker_id"),
                factChecker: this.getFormData("fact_checker"),
                published_for_id: this.getFormData("published_for_id", this.languageData.default_country_id),
                publishedFor: this.getFormData("published_for", this.languageData.default_country),
                casino_id: this.getFormData("casino_id"),
                casino: this.getFormData("casino"),
                gallery: this.getFormData("gallery", []),
                prosAndCons: this.getReferenceData("pros_and_cons"),
                pros_and_cons_list_id: this.getReferenceData("pros_and_cons_list_id"),
                similarCasinos: this.getReferenceData("similar_casinos"),
                bonuses: this.getReferenceData("bonuses"),
                slot_reviews: this.getReferenceData("slot_reviews"),
                game_reviews: this.getReferenceData("game_reviews"),
                image_id: this.getFormData("image_id"),
                image: this.getFormData("image"),
                image_trust_badge: this.getFormData("image_trust_badge"),
                image_link: this.getFormData("image_link"),
                created_at: this.getFormData("created_at") || new Date(),
                created_at_override: this.getFormData("created_at_override"),
                updated_at: this.getFormData("updated_at") || new Date(),
                updated_at_override: this.getFormData("updated_at_override"),
                two_col_four_table: this.getFormData("two_col_four_table"),
                table: this.getFormData("table", this.defaultTableData()),
                variants: this.getFormData("variants", []),
            },
        };
    },
    created() {
        if (!this.data?.id) {
            this.form.overview_trust_badge = 'approved';
            this.form.image_trust_badge = 'tested';
        }
    },
    computed: {
        excludedAuthors () {
            return [this.form.fact_checker_id];
        },
        excludedFactCheckers () {
            return [this.form.author_id];
        },
        baseDocumentCasinoId() {
            return this.form?.baseDocument?.casino_id;
        },
        casinoNotSelected(){
            return this.form.casino === null
        },
        excludedSimilarCasinos () {
            return [this.form.casino_id];
        },
    },
    watch: {
        "form.author": function (newValue) {
            this.form.author_id = newValue ? newValue.id : null;
        },
        "form.factChecker": function (newValue) {
            this.form.fact_checker_id = newValue ? newValue.id : null;
        },
        "form.publishedFor": function (newValue) {
                this.form.published_for_id = newValue ? newValue.id : null;
        },
        "form.casino": function (newValue) {
            this.form.casino_id = newValue ? newValue.id : null;
        },
        "form.prosAndCons": function (newValue) {
            this.form.pros_and_cons_list_id = newValue ? newValue.id : null;
        },
        "form.image": function (newValue) {
            this.form.image_id = newValue ? newValue.id : null;
        },
    }
};
</script>
